import { useCallback, useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, PlusCircle, Layers, ShoppingBag, ArrowLeftRight,
  Wallet, LifeBuoy, Bell, User, LogOut, Menu, X, Globe, Wrench,
} from "lucide-react";
import api, { clearToken, inr } from "./api";
import { EASE } from "@/components/Motion";
import { Logo } from "@/components/Logo";
import MimiWidget from "./MimiWidget";
import OfferPopup from "./OfferPopup";

const NAV = [
  {
    group: "OVERVIEW",
    items: [
      { to: "/panel", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/panel/new-order", label: "New Order", icon: PlusCircle, testid: "nav-new-order" },
      { to: "/panel/services", label: "Services", icon: Layers, testid: "nav-services" },
    ],
  },
  {
    group: "ORDERS",
    items: [
      { to: "/panel/orders", label: "My Orders", icon: ShoppingBag, testid: "nav-orders" },
      { to: "/panel/transactions", label: "Transactions", icon: ArrowLeftRight, testid: "nav-transactions" },
    ],
  },
  {
    group: "ACCOUNT",
    items: [
      { to: "/panel/add-funds", label: "Add Funds", icon: Wallet, testid: "nav-add-funds" },
      { to: "/panel/support", label: "Support Tickets", icon: LifeBuoy, testid: "nav-support" },
      { to: "/panel/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
      { to: "/panel/profile", label: "Profile", icon: User, testid: "nav-profile" },
    ],
  },
];

const SidebarContent = ({ onNavigate }) => (
  <div className="flex h-full flex-col">
    <Link to="/panel" onClick={onNavigate} data-testid="panel-logo" className="flex items-center gap-3 px-6 py-6">
      <Logo className="h-10 w-10 rounded-xl object-cover ring-1 ring-gold/40" />
      <div>
        <div className="text-sm font-bold tracking-[0.18em] text-neutral-100">
          BEATKING<span className="text-gold">SMM</span>
        </div>
        <div className="font-mono text-[9px] tracking-[0.3em] text-gold/70">CUSTOMER PANEL</div>
      </div>
    </Link>
    <nav className="flex-1 overflow-y-auto px-4 pb-6">
      {NAV.map((g) => (
        <div key={g.group} className="mt-6 first:mt-2">
          <div className="px-3 pb-2 font-mono text-[9px] tracking-[0.3em] text-neutral-600">{g.group}</div>
          {g.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={item.testid}
              className={({ isActive }) =>
                `mb-1 flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors duration-300 ${
                  isActive
                    ? "border-gold/30 bg-gold/[0.08] font-semibold text-goldlight"
                    : "border-transparent text-neutral-400 hover:bg-white/[0.03] hover:text-neutral-100"
                }`
              }
            >
              <item.icon size={16} strokeWidth={1.9} />
              {item.label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
    <div className="border-t border-white/5 p-4">
      <Link
        to="/"
        data-testid="nav-view-site"
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-neutral-500 transition-colors hover:text-goldlight"
      >
        <Globe size={16} /> View Website
      </Link>
    </div>
  </div>
);

export default function PanelLayout() {
  const [user, setUser] = useState(null);
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
  }, []);

  const refreshUnread = useCallback(async () => {
    const { data } = await api.get("/notifications");
    setUnread(data.unread);
  }, []);

  const [maintenance, setMaintenance] = useState(null);
  const [siteContent, setSiteContent] = useState(null);

  useEffect(() => {
    refreshUser().catch(() => {});
    refreshUnread().catch(() => {});
    api.get("/maintenance").then(({ data }) => setMaintenance(data)).catch(() => {});
    api.get("/content").then(({ data }) => setSiteContent(data)).catch(() => {});
  }, [refreshUser, refreshUnread]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* token cleared regardless */
    }
    clearToken();
    navigate("/panel/login");
  };

  if (maintenance?.enabled && user?.role !== "admin") {
    return (
      <div
        className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-ink px-6 text-center"
        data-testid="maintenance-screen"
      >
        <div className="pointer-events-none absolute left-1/2 top-1/4 h-72 w-72 -translate-x-1/2 rounded-full bg-gold/10 blur-[100px]" />
        <div className="noise-overlay pointer-events-none fixed inset-0 opacity-[0.05]" />
        <Logo className="relative h-16 w-16 rounded-2xl object-cover ring-1 ring-gold/40" />
        <div className="relative mt-6 flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] text-amber-300">
          <Wrench size={13} /> UNDER MAINTENANCE
        </div>
        <p className="relative mx-auto mt-5 max-w-md font-serif text-2xl italic leading-relaxed text-neutral-200">
          "{maintenance.message}"
        </p>
        <p className="relative mt-4 font-mono text-[9px] tracking-[0.25em] text-neutral-600">
          BEATKINGSMM 2.0 · BACK SHORTLY
        </p>
        <Link
          to="/"
          data-testid="maintenance-back-link"
          className="relative mt-8 font-mono text-[10px] tracking-[0.25em] text-neutral-500 transition-colors hover:text-goldlight"
        >
          ← BACK TO WEBSITE
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-neutral-100">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-white/5 bg-[#08080B] lg:block" data-testid="panel-sidebar">
        <SidebarContent onNavigate={() => {}} />
      </aside>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="fixed inset-y-0 left-0 z-50 w-64 border-r border-white/5 bg-[#08080B] lg:hidden"
              data-testid="panel-mobile-sidebar"
            >
              <SidebarContent onNavigate={() => setMenuOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b border-white/5 bg-ink/85 px-4 py-4 backdrop-blur-xl sm:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="panel-menu-button"
              aria-label="Open menu"
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-neutral-300 lg:hidden"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <span className="hidden font-mono text-[10px] tracking-[0.3em] text-neutral-600 sm:block">
              THE ENGINE ROOM
            </span>
          </div>

          <div className="flex items-center gap-3">
            {siteContent && (
              <span data-testid="topbar-site-status"
                className={`hidden items-center gap-1.5 rounded-full border px-3 py-2 font-mono text-[9px] tracking-[0.15em] sm:inline-flex ${
                  siteContent.orders_open
                    ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                    : "border-rose-400/30 bg-rose-400/10 text-rose-300"
                }`}>
                <span className={`h-1.5 w-1.5 rounded-full ${siteContent.orders_open ? "animate-pulse bg-emerald-400" : "bg-rose-400"}`} />
                {siteContent.orders_open ? "SERVICES OPEN" : "SERVICES CLOSED"}
              </span>
            )}
            <Link
              to="/panel/add-funds"
              data-testid="topbar-balance"
              className="flex items-center gap-2 rounded-full border border-gold/30 bg-gold/[0.07] px-4 py-2 text-sm font-semibold text-goldlight transition-colors duration-300 hover:border-gold/60"
            >
              <Wallet size={14} />
              {user ? inr(user.balance) : "—"}
            </Link>
            <Link
              to="/panel/notifications"
              data-testid="topbar-notifications"
              aria-label="Notifications"
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-neutral-300 transition-colors hover:border-gold/50 hover:text-goldlight"
            >
              <Bell size={16} />
              {unread > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[9px] font-bold text-black" data-testid="topbar-unread-badge">
                  {unread}
                </span>
              )}
            </Link>
            <Link
              to="/panel/profile"
              data-testid="topbar-profile"
              aria-label="Profile"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-goldlight to-[#996515] text-sm font-bold text-black"
            >
              {user?.name?.[0]?.toUpperCase() || "·"}
            </Link>
            <button
              onClick={logout}
              data-testid="logout-button"
              aria-label="Log out"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-neutral-400 transition-colors hover:border-rose-400/50 hover:text-rose-300"
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10" data-testid="panel-content">
          <Outlet context={{ user, refreshUser, refreshUnread }} />
        </main>

        <MimiWidget />
        <OfferPopup user={user} />

        {(siteContent?.whatsapp || "").replace(/\D/g, "") && (
          <a
            href={`https://wa.me/${siteContent.whatsapp.replace(/\D/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-float-button"
            aria-label="Chat on WhatsApp"
            className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.4)] transition-transform duration-300 hover:scale-110"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        )}
      </div>
    </div>
  );
}
